import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, MessageCircle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_NUMBER = "5562986293474";

// Função para gerar hash SHA-256 (necessária para Conversões Otimizadas do Google Ads)
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function ThankYou() {
  const navigate = useNavigate();
  const [leadData, setLeadData] = useState(null);
  const [converted, setConverted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [isRedirecting, setIsRedirecting] = useState(true);
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (processedRef.current) return;

    const token = sessionStorage.getItem("rl_conversion_token");
    const data = sessionStorage.getItem("rl_lead_data");

    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    // Mark as processed before doing anything
    processedRef.current = true;

    const parsedData = data ? JSON.parse(data) : null;
    setLeadData(parsedData);
    setConverted(true);

    // Clear token immediately so reload won't count again
    sessionStorage.removeItem("rl_conversion_token");
    sessionStorage.removeItem("rl_lead_data");

    // Processar Conversões Otimizadas e Redirecionamento
    const handleConversionAndRedirect = async () => {
      let hashedEmail = "";
      let hashedPhone = "";

      if (parsedData?.email) {
        hashedEmail = await sha256(parsedData.email);
      }
      if (parsedData?.telefone) {
        const cleanPhone = parsedData.telefone.replace(/\D/g, "");
        hashedPhone = await sha256(cleanPhone);
      }

      // 1. Preparar Mensagem do WhatsApp
      const msg =
        `Olá! Meu nome é ${parsedData?.nome || ""}.%0A` +
        `Telefone: ${parsedData?.telefone || ""}%0A` +
        (parsedData?.email ? `E-mail: ${parsedData.email}%0A` : "") +
        `Serviço: ${parsedData?.servico || ""}%0A` +
        (parsedData?.mensagem ? `Mensagem: ${parsedData.mensagem}%0A` : "") +
        `%0AGostaria de solicitar um orçamento.`;
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;

      // 2. Disparar Conversão com Callback ou Timeout
      const fireConversion = () => {
        return new Promise((resolve) => {
          if (window.gtag) {
            window.gtag("set", "user_data", {
              sha256_email_address: hashedEmail,
              sha256_phone_number: hashedPhone,
            });

            window.gtag("event", "conversion", {
              send_to: "AW-17990828766/XlWMCM2t8oIcEN6F2YJD",
              transport_type: "beacon",
              event_callback: () => resolve(true), // Callback do Google Ads
            });

            // Fallback caso o callback não responda em 1s
            setTimeout(() => resolve(true), 1000);
          } else {
            resolve(true);
          }
        });
      };

      // 3. Disparar DataLayer (GTM)
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "lead_form_submit",
        lead_service: parsedData?.servico || "",
        lead_name: parsedData?.nome || "",
        conversion_time: new Date().toISOString(),
        enhanced_conversion_data: {
          email: hashedEmail,
          phone: hashedPhone,
        }
      });

      // 4. Aguardar o envio da conversão antes de iniciar o contador visual
      await fireConversion();

      // 5. Iniciar Contador Regressivo para Redirecionamento
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsRedirecting(false);
            window.open(waUrl, "_blank");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    handleConversionAndRedirect();
  }, [navigate]);

  if (!converted) return null;

  return (
    <div
      data-testid="thankyou-page"
      className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4"
    >
      <div className="max-w-lg w-full bg-white rounded-sm shadow-xl p-10 text-center">
        {/* Success icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>

        <h1
          data-testid="thankyou-title"
          className="text-3xl md:text-4xl font-extrabold text-[#0F172A] font-['Barlow_Condensed'] uppercase tracking-tight"
        >
          Orçamento Solicitado!
        </h1>

        <p className="mt-4 text-base text-[#64748B] leading-relaxed font-['Manrope']">
          {leadData?.nome ? `${leadData.nome}, s` : "S"}ua solicitação foi enviada com sucesso. 
        </p>

        {/* Mensagem de Redirecionamento */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-sm flex flex-col items-center">
          {countdown > 0 ? (
            <>
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin mb-2" />
              <p className="text-sm text-blue-800 font-medium">
                Você será redirecionado para o WhatsApp em <strong>{countdown} segundos</strong>...
              </p>
            </>
          ) : (
            <p className="text-sm text-green-800 font-medium">
              Redirecionando para o WhatsApp agora!
            </p>
          )}
        </div>

        {leadData && (
          <div className="mt-6 bg-[#F0F9FF] border border-[#E0F2FE] rounded-sm p-4 text-left">
            <p className="text-xs font-bold text-[#0E86D4] font-['Barlow_Condensed'] uppercase tracking-wider mb-2">
              Resumo da Solicitação
            </p>
            <div className="space-y-1 text-sm text-[#334155] font-['Manrope']">
              <p><strong>Serviço:</strong> {leadData.servico}</p>
              <p><strong>Telefone:</strong> {leadData.telefone}</p>
              {leadData.email && <p><strong>E-mail:</strong> {leadData.email}</p>}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de solicitar um orçamento.`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              data-testid="thankyou-whatsapp-btn"
              className="bg-[#25D366] hover:bg-[#128C7E] text-white font-['Barlow_Condensed'] uppercase tracking-wider font-bold rounded-sm px-6 py-5"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Abrir WhatsApp Agora
            </Button>
          </a>
          <Button
            data-testid="thankyou-back-btn"
            variant="outline"
            className="border-2 border-[#0E86D4] text-[#0E86D4] hover:bg-[#0E86D4] hover:text-white font-['Barlow_Condensed'] uppercase tracking-wider font-bold rounded-sm px-6 py-5"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar ao Site
          </Button>
        </div>

        <p className="mt-6 text-xs text-[#94A3B8] font-['Manrope']">
          Se o WhatsApp não abriu automaticamente, clique no botão acima.
        </p>
      </div>
    </div>
  );
}
